import { useNavigate } from 'react-router-dom'
import { trackEvent } from 'analytics/trackFunctions'
import { INTEREST_FREE_ADVANCE_MAX_AMOUNT } from 'constants/credit'
import { moneyFormat } from 'utils'
import { HelpIcon, RateZeroIcon, SimpleArrowIcon } from 'assets/icons'
import RoundedIcon from './RoundedIcon'
import Badge from './Badge'

interface CardButtonProps {
  onClick: () => void
  icon: string
  title: string | JSX.Element
  className?: string
  classNameCard?: string
  description?: string
  classNameBtnText?: string
  classNameRoundedIcon?: string
  inactiveMessage?: string | JSX.Element
  activeUpperMessage?: string | JSX.Element
  disabled?: boolean
  badge?: JSX.Element
}

const CardButton = ({
  icon,
  title,
  onClick,
  className = '',
  classNameCard = '',
  description = null,
  classNameBtnText = '',
  classNameRoundedIcon = '',
  inactiveMessage = null,
  activeUpperMessage = null,
  disabled = false,
  badge = null
}: CardButtonProps) => {
  const image = (
    <img className={`w-6 ${!!disabled && 'grayscale'}`} src={icon} alt='' />
  )
  return (
    <div data-testid='cardButton' className={`w-full relative ${className}`}>
      {badge && !disabled ? (
        <span className='absolute z-10 -top-4 right-0'>{badge}</span>
      ) : null}
      {disabled ? (
        <div className='bg-blue-900 rounded-t-[10px] -mb-8 w-fit text-white font-bold text-[10px] px-[6px] min-w-[294px] min-h-[34px] z-0 pb-1 pt-[6px] pr-2'>
          {inactiveMessage}
        </div>
      ) : null}
      {activeUpperMessage ? (
        <div className='bg-green-800 rounded-t-[10px] -mb-2 w-fit text-white font-bold text-[10px] px-[6px] min-w-[294px] min-h-[34px] z-0 pb-1 pt-[6px] pr-2'>
          {activeUpperMessage}
        </div>
      ) : null}
      <button
        type='button'
        onClick={onClick}
        className={`${
          disabled ? 'bg-[#E4E4E4] mt-[22px]' : 'bg-white'
        } rounded-[10px] p-3 w-full flex items-center z-1 relative border border-gray-500 ${classNameCard}`}
      >
        <RoundedIcon
          className={`mr-3 !h-11 !w-11 ${classNameRoundedIcon} ${
            disabled && 'bg-white'
          }`}
          icon={image}
        />
        <div className='w-full flex flex-col justify-center text-xs text-left'>
          <span
            className={`${
              disabled ? 'text-[#7D7D7D]' : 'text-purple-900'
            } ${classNameBtnText} font-bold leading-4 mb-1`}
          >
            {title}
          </span>
          {!!description && (
            <span
              className={`text-2xs font-medium leading-4 ${
                disabled ? 'text-[#7E7E7E]' : 'text-black'
              } ${classNameBtnText}`}
            >
              {description}
            </span>
          )}
        </div>
        {disabled ? (
          <HelpIcon variant='BLUE' className='w-7 h-7 mr-3' />
        ) : (
          <span className='rounded-full shrink-0 flex items-center justify-center border border-gray-300 h-6 w-6'>
            <SimpleArrowIcon
              className='transition-all duration-200'
              stroke='#442E83'
            />
          </span>
        )}
      </button>
    </div>
  )
}
export default CardButton

interface NewCardButtonProps {
  onClick: () => void
  children: JSX.Element
  className?: string
  badge?: JSX.Element
}

export const NewCardButton = ({
  onClick,
  children,
  className = '',
  badge = null
}: NewCardButtonProps) => (
  <>
    {Boolean(badge) && badge}
    <button
      onClick={() => onClick()}
      type='button'
      className={`my-6 w-full transition-all duration-200 bg-white border shadow-card hover:shadow-card-hover border-gray-300 rounded-lg py-4 px-3 flex items-center justify-between text-left ${className}`}
    >
      <div>{children}</div>
      <span className='rounded-full transition-all duration-200 flex items-center justify-center border border-gray-300 h-6 w-6'>
        <SimpleArrowIcon
          className='transition-all transform duration-200'
          stroke='#442E83'
        />
      </span>
    </button>
  </>
)

interface RateZeroCardButtonProps {
  className?: string
  isDepleted?: boolean
  remainingAmount?: number
}

export const RateZeroCardButton = ({
  className = '',
  isDepleted = false,
  remainingAmount = null
}: RateZeroCardButtonProps) => {
  const navigate = useNavigate()

  return (
    <button
      type='button'
      onClick={() => {
        if (!isDepleted) {
          trackEvent({ eventName: 'bille_adelantos_boton_tasa_0' })
          return navigate('/pedir-adelanto/sin-interes')
        }
      }}
      className={`rounded-[10px] bg-[#F5F5F5] h-[3.375rem] p-3 w-full flex items-center relative gap-3 text-purple-900 ${
        isDepleted && 'cursor-default'
      } ${className}`}
    >
      {!isDepleted && (
        <Badge className='absolute !py-0 right-3 -top-2'>
          <span>Sin interés</span>
        </Badge>
      )}
      <RoundedIcon
        className='h-[1.875rem] w-[1.875rem]'
        icon={<RateZeroIcon />}
      />
      <div className='w-full text-left text-xs'>
        <p className='font-bold'>
          {isDepleted
            ? '¡Ya disfrutaste tus adelantos a tasa 0!'
            : `Pedí hasta ${
                remainingAmount > 0
                  ? moneyFormat(remainingAmount)
                  : moneyFormat(INTEREST_FREE_ADVANCE_MAX_AMOUNT)
              } a tasa 0`}
        </p>
        {isDepleted ? (
          <p className='text-2xs leading-4 text-gray-900 font-medium'>
            Tenés hasta {moneyFormat(INTEREST_FREE_ADVANCE_MAX_AMOUNT)} por mes
            sin interés.
          </p>
        ) : null}
      </div>
      {isDepleted ? null : (
        <span className='rounded-full shrink-0 flex items-center justify-center bg-green-800 h-6 w-6'>
          <SimpleArrowIcon stroke='white' />
        </span>
      )}
    </button>
  )
}
